import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPayrocSessionToken } from 'api/Transaction';

import { GENERIC_ERROR } from 'lib/constants/messages';

import { getAchStyles, getCardStyles } from '../lib/styles';
import { achFields, cardFields, getAchWrapperMap, getCardWrapperMap } from '../lib/fields';
import { getInvalidRequiredFields, markFieldError, syncFieldError } from '../lib/fieldValidity';

const PAYROC_HOSTED_FIELDS_SCRIPT = process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT;

export const usePayroc = ({
  type,
  containerRef,
  amount,
  handlePayment,
  validate,
  id,
  mode = 'payment',
  buttonText,
  secureTokenId,
  terminalPin,
  onError = () => {}
}) => {
  const [_dataBToken, setDataBToken] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldFormCard, setFieldFormCard] = useState(null);
  const [fieldFormACH, setFieldFormACH] = useState(null);

  const isCard = type === 'card';
  const isAch = type === 'ach';

  // Keeps every hosted field bound to this instance's own DOM nodes — two live
  // instances share the same class names and would otherwise fight over them.
  const scope = id ? `#${CSS.escape(id)} ` : '';
  // The submit button often renders outside the scoped container (modal footers),
  // so it is matched by a per-instance class instead of the scope prefix.
  const submitTarget = id ? `.payroc-submit-${CSS.escape(id)}` : undefined;

  const requiredFields = isCard
    ? ['cardholderName', 'cardNumber', 'cvv', 'expiryDate']
    : ['nameOnAccount', 'accountType', 'achAccountNumber', 'routingNumber'];

  const fieldValidityRef = useRef({});
  const handlePaymentRef = useRef(handlePayment);
  const validateRef = useRef(validate);

  useEffect(() => {
    handlePaymentRef.current = handlePayment;
  }, [handlePayment]);

  useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');

    try {
      script.src = PAYROC_HOSTED_FIELDS_SCRIPT;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () =>
        setError({ error: "Couldn't load the payment form. Please try again." });
      document.body.appendChild(script);
    } catch (err) {
      console.error('Error loading 3rd-party script:', err);
      setError({ error: "Couldn't load the payment form. Please try again." });
    }

    return () => {
      script.remove();
    };
  }, [id]);

  const initializeForm = useCallback(
    async (formFields, wrapperMap) => {
      const Payroc = window.Payroc;
      const fieldConfig = formFields?.fields?.card || formFields?.fields?.ach || {};

      // Requesting a session token before this guard burns a Payroc session on
      // every unmounted / not-yet-loaded init.
      if (!containerRef.current || !Payroc?.hostedFields) return;

      try {
        setIsLoading(true);
        const res = await getPayrocSessionToken(navigate, {
          scenario: mode,
          secureTokenId,
          terminalPin
        });
        const sessionToken = res?.token;

        if (!sessionToken) {
          const message = GENERIC_ERROR;
          setError({ error: message });
          onError(message);
          return;
        }

        setDataBToken(sessionToken);

        if (containerRef.current && Payroc.hostedFields && sessionToken) {
          const instance = new Payroc.hostedFields({
            ...formFields,
            sessionToken,
            onPreSubmit: async () => {
              const invalidRequired = getInvalidRequiredFields(
                requiredFields,
                fieldValidityRef.current
              );
              if (invalidRequired.length) {
                invalidRequired.forEach((f) => markFieldError(f, wrapperMap, fieldConfig));
                return false;
              }
              if (validateRef.current) {
                const isValid = await validateRef.current();
                return isValid;
              }
              return true;
            }
          });

          instance.on('error', ({ type, field, message }) => {
            const isConnectFailure = typeof message === 'string' && message.includes('to connect');
            if (isConnectFailure) {
              console.warn(`Payroc hosted field failed to connect (field=${field || 'unknown'})`, {
                field,
                type,
                message
              });
            }
            const errorMessage = isConnectFailure
              ? "Couldn't load the card form. Please try again."
              : message || "Couldn't load the payment form. Please try again.";
            setError({ error: errorMessage, type, field });
            onError(errorMessage);
          });

          await instance.initialize();

          instance.on('submissionSuccess', async (data) => {
            if (!handlePaymentRef.current) {
              setError({
                error: "This payment method isn't supported",
                type: 'error',
                field: 'payment_method'
              });
              return;
            }

            const result = await handlePaymentRef.current(data);
            if (result?.code !== 0) {
              setError(result);
              return;
            }
          });

          instance.on('fieldValidityChange', ({ field, validationError }) => {
            fieldValidityRef.current[field] = !validationError;
            syncFieldError(field, wrapperMap, fieldConfig, validationError);
          });

          return instance;
        }
      } catch (err) {
        const rawMessage = err?.error || err?.message || '';
        const isConnectFailure =
          typeof rawMessage === 'string' && rawMessage.includes('to connect');
        if (isConnectFailure) {
          console.warn('Payroc hosted fields failed to connect', { message: rawMessage });
        }
        const message = isConnectFailure
          ? "Couldn't load the card form. Please try again."
          : rawMessage || GENERIC_ERROR;
        setError({ error: message });
        onError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [containerRef]
  );

  const initCardForm = useCallback(async () => {
    if (fieldFormCard || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { card: cardFields({ amount, buttonText, scope, submitTarget }) },
      styles: getCardStyles()
    };

    const hostedFieldsFormCard = await initializeForm(formFields, getCardWrapperMap(scope));

    setFieldFormCard(hostedFieldsFormCard);
  }, [initializeForm, amount, scriptLoaded, buttonText, scope, submitTarget]);

  const initACHForm = useCallback(async () => {
    if (fieldFormACH || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { ach: achFields({ amount, buttonText, scope, submitTarget }) },
      styles: getAchStyles()
    };

    const hostedFieldsFormACH = await initializeForm(formFields, getAchWrapperMap(scope));

    setFieldFormACH(hostedFieldsFormACH);
  }, [initializeForm, amount, scriptLoaded, buttonText, scope, submitTarget]);

  const startInitialization = useCallback(() => {
    if (isAch) {
      initACHForm();
    } else if (isCard) {
      initCardForm();
    }
  }, [initACHForm, initCardForm, isCard, isAch]);

  useEffect(() => {
    startInitialization();
  }, [startInitialization]);

  const retry = useCallback(() => {
    setError(null);
    startInitialization();
  }, [startInitialization]);

  return {
    isLoading,
    error,
    retry
  };
};
